import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';
import { Paginator } from 'primereact/paginator';
import { Tag } from 'primereact/tag';
import ReactPlayer from 'react-player';
import type { VideoItem } from '../videos';
import { apiServices } from 'apps/marsinet/src/app/services/apiServices';
import { Button } from 'primereact/button';
import VideoEditTagsSimpleModal from './video-edit-tags-simple-modal';

interface VideosGridProps {
  videos: VideoItem[];
  loading: boolean;
  totalRecords: number;
  rows: number;
  first: number;
  /** Permiso resuelto por el padre con can(); la UI sólo lo consume. */
  canManageTags?: boolean;
  /** Colores de tags resueltos por el padre (evita fetch duplicado y 403). */
  tagColors?: Record<string, string>;
  onPageChange: (page: number) => void;
  onRowsChange: (rows: number) => void;
  onTagsUpdated?: (updatedVideo: VideoItem) => void;
}

function formatDuration(seconds?: number) {
  if (!seconds) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// Estilo reutilizable para limitar texto a N líneas, evitando desbordes
const clamp = (lines: number): React.CSSProperties => ({
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
  width: '100%',
});

const getTagStyle = (hexColor?: string): React.CSSProperties => {
  const color = hexColor || '#3B82F6';
  return {
    backgroundColor: color,
    borderColor: color,
    color: '#ffffff',
  };
};

const VideosGrid: React.FC<VideosGridProps> = ({
  videos,
  loading,
  totalRecords,
  rows,
  first,
  canManageTags = false,
  tagColors = {},
  onPageChange,
  onRowsChange,
  onTagsUpdated,
}) => {
  const { t } = useTranslation();
  const [selectedVideo, setSelectedVideo] = useState<VideoItem | null>(null);
  const [showPlayer, setShowPlayer] = useState(false);
  const [showEditTagsModal, setShowEditTagsModal] = useState(false);
  const [videoForEditTags, setVideoForEditTags] = useState<VideoItem | null>(
    null
  );
  const [sasUrls, setSasUrls] = useState<Record<string, string>>({});
  const [loadingVideoId, setLoadingVideoId] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.setAttribute('controlsList', 'nodownload');
    v.disablePictureInPicture = true;
  }, [showPlayer, selectedVideo?.id]);

  const prefetchSasUrl = async (video: VideoItem) => {
    if (sasUrls[video.id] || loadingVideoId) return;
    try {
      const response =
        await apiServices.videosApi.videosControllerGenerateShareUrl(
          video.id,
          '60'
        );
      const shareUrl = (response.data as any).shareUrl;
      setSasUrls((prev) => ({ ...prev, [video.id]: shareUrl }));
    } catch {
      // Silencioso: si falla el prefetch, se reintentará al hacer clic
    }
  };

  const handlePlayVideo = async (video: VideoItem) => {
    setSelectedVideo(video);

    // Si ya tenemos la URL, mostrar el player
    if (sasUrls[video.id]) {
      setShowPlayer(true);
      return;
    }
    setLoadingVideoId(video.id);
    try {
      const response =
        await apiServices.videosApi.videosControllerGenerateShareUrl(
          video.id,
          '60'
        );
      const shareUrl = (response.data as any).shareUrl;
      setSasUrls((prev) => ({ ...prev, [video.id]: shareUrl }));
      setShowPlayer(true);
    } catch (error) {
      console.error(`Error getting SAS URL for ${video.id}:`, error);
      setSasUrls((prev) => ({ ...prev, [video.id]: `/videos/${video.id}` }));
      setShowPlayer(true);
    } finally {
      setLoadingVideoId(null);
    }
  };

  const closeFullscreen = () => {
    setShowPlayer(false);
    setSelectedVideo(null);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showPlayer) closeFullscreen();
    };
    if (showPlayer) document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showPlayer]);

  if (loading && videos.length === 0) {
    return (
      <div
        className="w-full"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '1.5rem',
        }}
      >
        {Array.from({ length: rows > 6 ? 6 : rows }).map((_, i) => (
          <div
            key={i}
            className="rounded-lg border border-gray-200 overflow-hidden animate-pulse"
          >
            <div
              style={{
                width: '100%',
                paddingTop: '56.25%',
                backgroundColor: '#e5e7eb',
                position: 'relative',
              }}
            />
            <div className="p-3 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="h-3 bg-gray-200 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!loading && videos.length === 0) {
    return (
      <div className="w-full flex flex-col items-center justify-center py-16 text-center">
        <i className="pi pi-video text-5xl text-gray-300 mb-4" />
        <p className="text-gray-500 font-medium">
          {t('common.no_videos')}
        </p>
      </div>
    );
  }

  return (
    <>
      <div
        className="w-full relative"
        style={{
          opacity: loading ? 0.5 : 1,
          pointerEvents: loading ? 'none' : 'auto',
          transition: 'opacity 0.15s ease',
        }}
      >
        {loading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 2,
            }}
          >
            <i className="pi pi-spin pi-spinner text-gray-500 text-2xl mt-4" />
          </div>
        )}

        <div
          className="w-full"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '1.5rem',
          }}
        >
          {videos.map((video, index) => {
            const durationLabel = formatDuration(video.duration);
            const cardTitle =
              video.title ||
              t('common.default_video_title', { number: first + index + 1 });

            return (
              <div
                key={video.id}
                className="rounded-lg border border-gray-200 overflow-hidden shadow-sm hover:shadow-lg transition-shadow bg-white"
                style={{
                  position: 'relative', // necesario para posicionar el botón respecto a la card
                  minWidth: 0,
                  width: '100%',
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {canManageTags && (
                  <div
                    className="flex gap-2"
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      zIndex: 3,
                    }}
                  >
                    <Button
                      icon="pi pi-pencil"
                      className="p-button-rounded p-button-text"
                      style={{
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        color: 'white',
                        width: '32px',
                        height: '32px',
                      }}
                      tooltip={t('common.edit_tags')}
                      onClick={() => {
                        setVideoForEditTags(video);
                        setShowEditTagsModal(true);
                      }}
                    />
                  </div>
                )}
                {/* Portada del vídeo */}
                <div
                  role="button"
                  tabIndex={0}
                  aria-label={t('common.play_video', { title: cardTitle })}
                  className="relative cursor-pointer group"
                  style={{
                    width: '100%',
                    paddingTop: '56.25%', // 16:9
                    position: 'relative',
                    overflow: 'hidden',
                    backgroundColor: '#111827',
                    borderRadius: '0.5rem', // redondea solo las esquinas superiores
                  }}
                  onClick={() => handlePlayVideo(video)}
                  onMouseEnter={() => prefetchSasUrl(video)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handlePlayVideo(video);
                    }
                  }}
                >
                  <img
                    src={video.thumbnail || '/assets/portada_fallback.png'}
                    alt={cardTitle}
                    loading="lazy"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />

                  <div
                    className="group-hover:bg-opacity-40 transition-all"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      backgroundColor: 'rgba(0,0,0,0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <div
                      className="transform group-hover:scale-110 transition-transform"
                      style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '9999px',
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <i
                        className="pi pi-play text-white text-2xl"
                        style={{ marginLeft: '3px' }}
                      />
                    </div>
                  </div>

                  {durationLabel && (
                    <span
                      style={{
                        position: 'absolute',
                        bottom: '8px',
                        right: '8px',
                        backgroundColor: 'rgba(0,0,0,0.75)',
                        color: 'white',
                        fontSize: '0.75rem',
                        padding: '2px 6px',
                        borderRadius: '4px',
                      }}
                    >
                      {durationLabel}
                    </span>
                  )}

                  {loadingVideoId === video.id && (
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(0,0,0,0.6)',
                      }}
                    >
                      <i className="pi pi-spin pi-spinner text-white text-2xl" />
                    </div>
                  )}
                </div>

                {/* ---- AQUÍ: título y descripción, justo debajo de la miniatura ---- */}
                <div
                  className="p-3 flex flex-col gap-1 flex-1"
                  style={{
                    minWidth: 0,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <h3
                    className="font-semibold text-base leading-snug text-gray-900"
                    style={{ ...clamp(2), margin: 0 }}
                    title={cardTitle}
                  >
                    {cardTitle}
                  </h3>

                  {video.description && (
                    <p
                      className="text-gray-500 text-base"
                      style={{ ...clamp(2), margin: 0 }}
                    >
                      {video.description}
                    </p>
                  )}
                  {/* ---- FIN bloque título/descripción ---- */}

                  {video.resolution && (
                    <div className="text-xs text-gray-400 mt-auto pt-1">
                      {video.resolution}
                    </div>
                  )}

                  {canManageTags && video.tags && video.tags.length > 0 && (
                    <div className="flex gap-1 flex-wrap pt-1">
                      {video.tags.slice(0, 3).map((tag) => (
                        <div
                          key={tag}
                          className="flex items-center gap-1 text-xs"
                        >
                          <Tag
                            value={tag}
                            className="text-xs"
                            style={getTagStyle(tagColors[tag])}
                          />
                        </div>
                      ))}
                      {video.tags.length > 3 && (
                        <span className="text-xs text-gray-400">
                          +{video.tags.length - 3}
                        </span>
                      )}
                    </div>
                  )}
                </div>

              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-6">
        <Paginator
          first={first}
          rows={rows}
          totalRecords={totalRecords}
          rowsPerPageOptions={[10, 20, 30, 50]}
          onPageChange={(e) => {
            onPageChange(e.page);
            if (e.rows !== rows) onRowsChange(e.rows);
          }}
        />
      </div>

      <Dialog
        visible={showPlayer}
        onHide={closeFullscreen}
        maximizable
        modal
        blockScroll
        header={selectedVideo?.title || selectedVideo?.filename}
      >
        {selectedVideo && (
          <div style={{ aspectRatio: '16 / 9', backgroundColor: 'black' }}>
            <video
              src={sasUrls[selectedVideo.id] || `/videos/${selectedVideo.id}`}
              controls
              controlsList="nodownload"
              disablePictureInPicture
              style={{ display: 'block', width: '100%', height: '100%' }}
            />
          </div>
        )}
      </Dialog>

      <VideoEditTagsSimpleModal
        visible={showEditTagsModal}
        onHide={() => {
          setShowEditTagsModal(false);
          setVideoForEditTags(null);
        }}
        video={videoForEditTags}
        onTagsUpdated={(updatedVideo) => {
          // Update the local reference to the edited video
          if (updatedVideo?.id) {
            setVideoForEditTags(updatedVideo);
            // Notify parent to update the list
            onTagsUpdated?.(updatedVideo);
          }
        }}
      />
    </>
  );
};

export default VideosGrid;